import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '../pim/Drawer';
import FieldWidget from '../pim/FieldWidget';
import { Button } from '../ui';
import { getProduct, saveProductValues } from '../../services/pimApi';
import { claimGate, decideGate, releaseGate } from '../../services/workflowApi';

/*
 * Review one gate.
 *
 * The point of doing this in a drawer rather than sending approvers to the
 * product editor is that a rejection is usually just a missing field. The gate
 * carries the list of attributes it covers, so they are rendered here as real
 * editors: the approver fills the gap and approves in one pass instead of
 * bouncing the item back to whoever entered it.
 */

const GLOBAL = 0;

const scopeFor = (attr, channelId, localeId) => ({
  channel_id: attr.is_scopable ? channelId : GLOBAL,
  locale_id: attr.is_localizable ? localeId : GLOBAL,
});

const keyOf = (code, channelId, localeId) => `${code}|${channelId}|${localeId}`;

/* The (channel, locale) pairs a gate's values live at, mirroring
   waterco.pim.workflow.scope_targets. A locale-group gate covers several
   markets that share one copy, so each is offered for editing. */
function scopeTargets(gate, channels) {
  if (gate.channel_id) {
    const ch = channels.find((c) => c.id === gate.channel_id);
    return ch ? [ch] : [];
  }
  if (gate.locale_group && gate.locale_group !== '*') {
    const members = gate.locale_group.split('+');
    return channels.filter((c) => members.includes(c.market_code));
  }
  return [{ id: GLOBAL, market_code: null, default_locale_id: GLOBAL }];
}

export default function ApprovalDrawer({ gate, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await getProduct(gate.sku));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load this product.');
    } finally {
      setLoading(false);
    }
  }, [gate.sku]);

  useEffect(() => {
    load();
  }, [load]);

  const covered = useMemo(() => {
    const codes = gate.covered_attributes || [];
    const all = detail?.attributes || [];
    // Preserve the gate's declared order, and skip anything the product's
    // family does not carry.
    return codes.map((code) => all.find((a) => a.code === code)).filter(Boolean);
  }, [gate.covered_attributes, detail]);

  const valueIndex = useMemo(() => {
    const index = {};
    (detail?.values || []).forEach((v) => {
      index[keyOf(v.attribute_code, v.channel_id, v.locale_id)] = v.value;
    });
    return index;
  }, [detail]);

  const targets = useMemo(
    () => scopeTargets(gate, detail?.channels || []),
    [gate, detail],
  );

  const valueAt = (attr, channelId, localeId) => {
    const scope = scopeFor(attr, channelId, localeId);
    const editKey = keyOf(attr.code, scope.channel_id, scope.locale_id);
    if (editKey in edits) return edits[editKey];
    for (const [c, l] of [
      [channelId, localeId],
      [channelId, GLOBAL],
      [GLOBAL, localeId],
      [GLOBAL, GLOBAL],
    ]) {
      const k = keyOf(attr.code, c, l);
      if (k in valueIndex) return valueIndex[k];
    }
    return null;
  };

  const setValueAt = (attr, channelId, localeId, val) => {
    const scope = scopeFor(attr, channelId, localeId);
    setEdits((prev) => ({ ...prev, [keyOf(attr.code, scope.channel_id, scope.locale_id)]: val }));
  };

  const dirty = Object.keys(edits).length;

  const saveEdits = async () => {
    if (!dirty) return true;
    const values = Object.entries(edits).map(([k, value]) => {
      const [attribute_code, channel_id, locale_id] = k.split('|');
      return { attribute_code, value, channel_id: Number(channel_id), locale_id: Number(locale_id) };
    });
    await saveProductValues(gate.sku, values);
    setEdits({});
    return true;
  };

  const run = async (fn, successMessage) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged?.(successMessage);
    } catch (e) {
      const detailErr = e?.response?.data?.detail;
      setError(typeof detailErr === 'string' ? detailErr : detailErr?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const decide = (action) =>
    run(async () => {
      // Save first: approving against values the approver has edited but not
      // saved would bake the wrong hash into the decision.
      await saveEdits();
      await decideGate(gate.id, action, comment || null);
      onClose();
    }, action === 'approve' ? `Approved ${gate.sku}` : `Rejected ${gate.sku}`);

  const missing = gate.missing_attributes || [];

  const footer = (
    <div className="apr-drawer-foot">
      <div className="apr-drawer-foot-left">
        {gate.status === 'pending' && (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => run(() => claimGate(gate.id, gate.version), `Claimed ${gate.sku}`)}
          >
            Claim
          </Button>
        )}
        {gate.status === 'in_review' && (
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => run(() => releaseGate(gate.id), `Released ${gate.sku}`)}
          >
            Release
          </Button>
        )}
        <Link className="apr-drawer-link" to={`/pim/products/${encodeURIComponent(gate.sku)}`}>
          Open full editor
        </Link>
      </div>
      <div className="apr-drawer-foot-right">
        <Button variant="ghost" disabled={busy} onClick={() => decide('reject')}>
          Reject
        </Button>
        <Button variant="primary" disabled={busy} onClick={() => decide('approve')}>
          {dirty ? `Save ${dirty} & approve` : 'Approve'}
        </Button>
      </div>
    </div>
  );

  return (
    <Drawer
      title={`${gate.gate_label || gate.gate_code} · ${gate.sku}`}
      sub={gate.product_name || undefined}
      onClose={onClose}
      footer={footer}
    >
      {error && <div className="apr-alert bad">{error}</div>}

      <div className="apr-drawer-meta">
        <span className={`apr-chip ${gate.status}`}>{gate.status.replace('_', ' ')}</span>
        {gate.market_code && <span className="apr-chip scope">{gate.market_code}</span>}
        {gate.locale_group && gate.locale_group !== '*' && (
          <span className="apr-chip scope">{gate.locale_group}</span>
        )}
        {gate.is_stale && (
          <span className="apr-chip stale" title="Covered values changed after this was approved">
            Changed since approval
          </span>
        )}
      </div>

      {missing.length > 0 && (
        <div className="apr-alert warn">
          Missing: {missing.join(', ')}. Fill them below and approve in one step.
        </div>
      )}

      {loading && <p className="apr-muted">Loading product…</p>}

      {!loading && covered.length === 0 && (
        <p className="apr-muted">
          This gate covers no attributes on this product&apos;s family. Review it in the full editor.
        </p>
      )}

      {!loading &&
        covered.map((attr) => (
          <div className="apr-field-group" key={attr.code}>
            <div className="apr-field-head">
              <span className="apr-field-label">{attr.label || attr.code}</span>
              {missing.includes(attr.code) && <span className="apr-field-missing">missing</span>}
            </div>
            {targets.map((target) => {
              const localeId = target.default_locale_id || GLOBAL;
              const showScope = targets.length > 1 && (attr.is_scopable || attr.is_localizable);
              return (
                <div className="apr-field-scope" key={`${attr.code}-${target.id}`}>
                  {showScope && <span className="apr-scope-tag">{target.market_code || 'Global'}</span>}
                  <FieldWidget
                    sku={gate.sku}
                    attribute={attr}
                    value={valueAt(attr, target.id, localeId)}
                    onChange={(v) => setValueAt(attr, target.id, localeId, v)}
                    scope={{ channel_id: target.id, locale_id: localeId }}
                  />
                </div>
              );
            })}
          </div>
        ))}

      <div className="apr-field-group">
        <div className="apr-field-head">
          <span className="apr-field-label">Comment</span>
        </div>
        <textarea
          className="apr-comment"
          rows={3}
          placeholder="Optional. Required in spirit when rejecting — say what is wrong."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
    </Drawer>
  );
}
